import { cn } from '../../../lib/cn';

/* =============================================================================
   The shared control skin.
   -----------------------------------------------------------------------------
   Every text input in this folder is the same box with the same state ladder
   (idle / hover / focus / disabled / readonly / error / success), so the class
   strings live here once. Two shapes consume them:

     - `controlBoxClass`   — the control *is* the box (a bare <input>/<textarea>).
     - `controlShellClass` — a wrapper is the box and holds a borderless control
                             plus siblings (a stepper, an icon, a prefix span).
                             Its ring is drawn with `focus-within`, and the inner
                             control opts out of the global ring via
                             `data-focus-ring="none"` (global.css honours it).

   No raw colour appears here or anywhere else in the folder — only token
   utilities, so every state flips with the theme.
   ============================================================================= */

export type ControlSize = 'sm' | 'md' | 'lg';

/** CONVENTIONS §5 — mandatory on the interactive element itself. */
export const CONTROL_FOCUS =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

/** The same ring, carried by a shell that wraps a focus-ring-less control. */
export const SHELL_FOCUS =
  'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-focus';

/**
 * The control heights ARE the geometry tokens: 32 / 40 / 48. Every form control
 * in the library — text and value inputs, `Select`, the pickers, the table's
 * selects — is 40px with a 1px border, and it takes that geometry from here
 * rather than restating it. Four different answers used to coexist (40/1px,
 * 40/2px, 48/2px, 44/1px), which is why two fields side by side had different
 * edges.
 */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-[var(--control-h-sm)]',
  md: 'h-[var(--control-h-md)]',
  lg: 'h-[var(--control-h-lg)]',
};

/** The control boundary width, from the token, never from Tailwind's scale. */
export const CONTROL_BORDER = 'border-[length:var(--border-w)]';

const TEXT_SIZE: Record<ControlSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-md',
};

const PADDING: Record<ControlSize, string> = {
  sm: 'px-2.5',
  md: 'px-3',
  lg: 'px-3.5',
};

export interface ControlStateOptions {
  size?: ControlSize;
  /** Red border + `aria-invalid` on the control. */
  invalid?: boolean;
  /** Green border after a successful inline save. */
  success?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

const BASE = cn(
  'w-full min-w-0 rounded-xs text-body',
  CONTROL_BORDER,
  'transition-[border-color,background-color,color,box-shadow]',
  'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
  'placeholder:text-muted',
);

/**
 * `:focus-within` matches the element itself when it holds focus, so one
 * variant covers both the bare control and the shell — and keeping the border
 * change out of the invalid/success branches stops the focus colour from
 * erasing the state colour.
 */
function stateClass({ invalid, success, disabled, readOnly }: ControlStateOptions): string {
  // Disabled is carried by the token ladder, not by an opacity step. The old
  // `opacity-60` composited `--text-muted` onto `--surface-muted` and then
  // blended the whole result with the page, which measured 2.36:1 in light and
  // 2.87:1 in dark on the shell's own text (axe, /components, InputUrl's
  // `https://` ghost prefix). The muted surface, the lighter `--border` and the
  // `not-allowed` cursor already say "inactive" without it.
  if (disabled) return 'cursor-not-allowed border-line bg-surface-muted text-muted';
  if (readOnly) return 'cursor-default border-line bg-surface-muted text-body';
  if (invalid) return 'border-danger bg-surface focus-within:border-danger';
  if (success) return 'border-success bg-surface hover:border-heading focus-within:border-heading';
  return 'border-line-strong bg-surface hover:border-heading focus-within:border-heading';
}

/**
 * The control itself is the box: a real `<input>` / `<textarea>`.
 *
 * `relative z-10` is load-bearing, and it is what makes `Field`'s 44px hit
 * target safe. `Field` gives the control row the `dhd-target` overlay, and that
 * overlay is a positioned pseudo-element generated *after* the row's children —
 * so a statically positioned control would be painted underneath it and every
 * press would land on the overlay instead of the control. The overlay's
 * `preventDefault` would then cancel the browser's own caret placement, killing
 * caret-by-click, drag-select and any consumer `onMouseDown`/`onClick`.
 * Lifting the control above the overlay leaves the overlay doing the one job it
 * is for: catching presses in the few pixels of padding around the control.
 */
export function controlBoxClass(options: ControlStateOptions = {}): string {
  const size = options.size ?? 'md';
  return cn(
    BASE,
    'relative z-10 appearance-none',
    CONTROL_HEIGHT[size],
    PADDING[size],
    TEXT_SIZE[size],
    CONTROL_FOCUS,
    stateClass(options),
  );
}

/** A wrapper is the box, holding a borderless control and its siblings. */
export function controlShellClass(options: ControlStateOptions = {}): string {
  const size = options.size ?? 'md';
  return cn(
    BASE,
    // See `controlBoxClass` — the shell (and so everything inside it) has to sit
    // above `Field`'s hit-target overlay, or the inner control is unreachable.
    'relative z-10 flex cursor-text items-center gap-2',
    CONTROL_HEIGHT[size],
    PADDING[size],
    SHELL_FOCUS,
    stateClass(options),
  );
}

/**
 * The borderless control that lives inside a shell. Pair it with
 * `data-focus-ring="none"` so the shell's `focus-within` ring is the only one
 * drawn.
 */
export const SHELL_CONTROL = cn(
  'h-full min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-inherit',
  'placeholder:text-muted focus:outline-none disabled:cursor-not-allowed',
);

/** The small adjunct buttons a shell may hold (a stepper, a clear button). */
export const SHELL_AFFIX =
  'shrink-0 select-none text-muted transition-colors duration-[var(--dur-fast)]';
